import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Alert, Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { Upload } from 'react-bootstrap-icons';
import { GroupedClientSelector } from '@/components/tools/GroupedClientSelector';
import { deleteDeploymentGroup, renameDeploymentGroup, saveDeploymentGroup } from '@/services/deploymentService';
import type { Client, DeploymentGroup } from '@/types';
import { parseClientNamesFromCSV } from '@/utils/csvUtils';
import { formatNzDateTime } from '@/utils/relativeTime';
import './deployments.css';

interface GroupEditorModalProps {
  show: boolean;
  /** The group to change, or null to make a new one. */
  group: DeploymentGroup | null;
  /** Every group, to refuse a name that is taken. */
  groups: DeploymentGroup[];
  clients: Client[];
  defaultConcurrency: number;
  maxConcurrency: number;
  onClose: () => void;
  /** The group was saved under this name. */
  onSaved: (groupName: string) => void;
  onDeleted: () => void;
}

interface GroupForm {
  groupName: string;
  clients: string[];
  description: string;
  maxConcurrency?: number;
}

const EMPTY: GroupForm = { groupName: '', clients: [], description: '', maxConcurrency: undefined };

/** Make, change, rename or delete a named set of clients to deploy together. */
export function GroupEditorModal({
  show,
  group,
  groups,
  clients,
  defaultConcurrency,
  maxConcurrency,
  onClose,
  onSaved,
  onDeleted,
}: GroupEditorModalProps) {
  const [form, setForm] = useState<GroupForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [csvFeedback, setCsvFeedback] = useState<string | null>(null);
  const csvInput = useRef<HTMLInputElement>(null);
  const clamp = (value: number) => Math.max(1, Math.min(value, maxConcurrency));

  useEffect(() => {
    if (!show) return;
    setForm(
      group
        ? {
            groupName: group.groupName,
            clients: [...group.clients],
            description: group.description ?? '',
            maxConcurrency: group.maxConcurrency,
          }
        : EMPTY
    );
    setError(null);
    setCsvFeedback(null);
  }, [show, group]);

  const close = () => {
    if (!busy) onClose();
  };

  const handleCsvUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFeedback(null);
    try {
      const parsedNames = parseClientNamesFromCSV(await file.text());
      if (parsedNames.length === 0) {
        setCsvFeedback('No client names found. The file needs a "Client Name" column.');
        return;
      }
      const known = new Set(clients.map((c) => c.name));
      const matched = parsedNames.filter((name) => known.has(name));
      const unmatched = parsedNames.length - matched.length;
      // Add to what is already ticked. An upload never unticks a client.
      setForm((prev) => ({ ...prev, clients: [...new Set([...prev.clients, ...matched])] }));
      setCsvFeedback(
        unmatched > 0
          ? `Added ${matched.length} clients. ${unmatched} names in the file match no known client.`
          : `Added ${matched.length} clients from the file.`
      );
    } catch {
      setCsvFeedback('Could not read that file as CSV.');
    } finally {
      // Clear the input so the same file can be picked again.
      e.target.value = '';
    }
  };

  const handleConcurrencyChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setForm((prev) => ({ ...prev, maxConcurrency: undefined }));
      return;
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return;
    setForm((prev) => ({ ...prev, maxConcurrency: clamp(parsed) }));
  };

  const handleSave = async () => {
    if (busy) return;
    if (group?.managed) {
      setError('This group is kept up to date by itself and cannot be changed.');
      return;
    }
    const name = form.groupName.trim();
    if (!name) {
      setError('Give the group a name.');
      return;
    }
    if (form.clients.length === 0) {
      setError('Tick at least one client.');
      return;
    }
    // The group being edited is left out, so saving it under its own name is not a clash.
    const clash = groups.find(
      (g) => g.groupName.toLowerCase() === name.toLowerCase() && g.groupName !== group?.groupName
    );
    if (clash) {
      setError('A group with this name already exists.');
      return;
    }
    setBusy(true);
    try {
      const payload: DeploymentGroup = {
        groupName: name,
        clients: Array.from(new Set(form.clients.map((c) => c.trim()).filter(Boolean))).sort(),
        description: form.description?.trim() || undefined,
        maxConcurrency: form.maxConcurrency ? clamp(form.maxConcurrency) : undefined,
        createdAt: group?.createdAt,
      };
      // The name is the group's key, so a rename is a copy to the new name and a delete of the old one.
      if (group && group.groupName !== name) await renameDeploymentGroup(group.groupName, payload);
      else await saveDeploymentGroup(payload);
      onSaved(name);
    } catch (e) {
      console.error('Failed to save deployment group:', e);
      setError(e instanceof Error ? e.message : 'Could not save the group. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!group || busy) return;
    if (group.managed) {
      setError('This group is kept up to date by itself and cannot be deleted.');
      return;
    }
    if (!confirm(`Delete the group "${group.groupName}"? Past runs of it stay in the history.`)) return;
    setBusy(true);
    try {
      await deleteDeploymentGroup(group.groupName);
      onDeleted();
    } catch (e) {
      console.error('Failed to delete deployment group:', e);
      setError(e instanceof Error ? e.message : 'Could not delete the group. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={close} size="lg" centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>{group ? `Edit group: ${group.groupName}` : 'New deployment group'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Group name</Form.Label>
          <Form.Control
            type="text"
            value={form.groupName}
            onChange={(e) => setForm((prev) => ({ ...prev, groupName: e.target.value }))}
            placeholder="Tier 1 customers"
            disabled={busy || Boolean(group?.managed)}
          />
          <Form.Text>
            {group?.managed
              ? 'This group is kept up to date by itself and cannot be renamed.'
              : group
                ? 'A new name must not be in use. Past runs keep the old name.'
                : 'The name must not be in use.'}
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-3">
          <div className="deploy-group-editor__clients-head">
            <Form.Label className="mb-0">Clients</Form.Label>
            <Button variant="outline-secondary" size="sm" onClick={() => csvInput.current?.click()} disabled={busy}>
              <Upload className="me-1" /> Upload CSV
            </Button>
            <input type="file" ref={csvInput} accept=".csv" hidden onChange={handleCsvUpload} />
          </div>
          {csvFeedback && (
            <Alert variant="info" className="py-1 mb-2">
              {csvFeedback}
            </Alert>
          )}
          <GroupedClientSelector
            clients={clients}
            selectedClientNames={form.clients}
            onClientToggle={(name) =>
              setForm((prev) => ({
                ...prev,
                clients: prev.clients.includes(name) ? prev.clients.filter((c) => c !== name) : [...prev.clients, name],
              }))
            }
            onSelectClients={(names) => setForm((prev) => ({ ...prev, clients: names }))}
            disabled={busy}
          />
        </Form.Group>
        <div className="deploy-group-editor__grid">
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Notes about this group (optional)"
              disabled={busy}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Clients deployed at once</Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                min={1}
                max={maxConcurrency}
                value={form.maxConcurrency ?? ''}
                onChange={handleConcurrencyChange}
                placeholder={String(defaultConcurrency)}
                disabled={busy}
              />
              <InputGroup.Text>clients</InputGroup.Text>
            </InputGroup>
            <Form.Text>
              Leave empty to use the portal default of {defaultConcurrency}. The most allowed is {maxConcurrency}.
            </Form.Text>
            {group && (
              <p className="deploy-group-editor__meta">
                {group.clients.length} clients. Last changed{' '}
                {group.updatedAt ? formatNzDateTime(group.updatedAt) : 'at an unknown time'}.
              </p>
            )}
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {group && (
          <Button variant="outline-danger" className="me-auto" onClick={handleDelete} disabled={busy}>
            Delete group
          </Button>
        )}
        <Button variant="outline-secondary" onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={busy}>
          {busy && <Spinner size="sm" className="me-2" />}
          Save group
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default GroupEditorModal;
